// React
import { useEffect, useState, useContext, useRef } from "react";

// React context and state management
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Third-party libraries
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  Alert,
} from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import PersonIcon from "@mui/icons-material/Person";
import { BarChart } from "@mui/x-charts/BarChart";
import { chartsTooltipClasses } from "@mui/x-charts";
import CloseIcon from "@mui/icons-material/Close";

import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { geoEqualEarth, geoCentroid } from "d3-geo";
import * as topojsonClient from "topojson-client";

import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import CommonSnackBar from "../shared-components/snackbar/commonSnackbar";
import SnackState from "../shared-components/snackbar/snackState";

// Project-specific imports
import { selectUserDetail } from "../redux/slice/userSlice";
import urlConstant from "../../src/constants/urlConstants";
import dataConstant from "../../src/constants/dataConstants";
import { httpGet } from "../../src/services/commonService";

const geoUrl = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 0,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "var(--black)",
    ...theme.applyStyles("dark", {
      backgroundColor: "var(--black)",
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 0,
    backgroundColor: "var(--black)",
    ...theme.applyStyles("dark", {
      backgroundImage: "linear-gradient(to right,rgb(255, 72, 0), #58B84A)",
    }),
  },
}));

const MIN_BAR_VALUE = 0;
const fillChartData = (arr) => {
  if (!Array.isArray(arr)) return Array(5).fill({ display: MIN_BAR_VALUE, real: 0 });
  return Array(5)
    .fill(0)
    .map((_, i) => {
      const real = Number(arr[i]);
      const isValidNumber = !isNaN(real);
      return {
        display: isValidNumber && real > 0 ? real : MIN_BAR_VALUE,
        real: isValidNumber ? real : 0,
      };
    });
};
const getYAxisMax = (dataArr) => {
  const maxReal = Math.max(...dataArr.map(d => d.real), 0);
  return maxReal > 1 ? Math.ceil(maxReal * 1) : 1;
};

const CustomBarChart = styled(BarChart)(({ theme }) => ({
  "& .MuiBarElement-root": {
    width: '12px !important',
    rx: "6px !important",
    ry: "6px !important",
    [theme.breakpoints.down("xl")]: {
      transform: "translateX(11px) !important",
    },
    [theme.breakpoints.down("sm")]: {
      transform: "translateX(9px) !important",
    },
     "@media (max-width:2560px)": {
      transform: "translateX(18px) !important",
    },            
    "@media (max-width:1440px)": {
      transform: "translateX(12px) !important",
    },
     "@media (max-width:1024px)": {
      transform: "translateX(4px) !important",
    },
    "@media (max-width:425px)": {
      transform: "translateX(12px) !important",
    },
    "@media (max-width:768px)": {
      transform: "translateX(12px) !important",
    },    
    "@media (max-width:375px)": {
      transform: "translateX(6px) !important",
    },
    "@media (max-width:320px)": {
      transform: "translateX(1px) !important",
    },
  },
  "& .MuiChartsAxisHighlight-root": {
    display: "none !important",
  },
}));

const Home = () => {
  const navigate = useNavigate();
  const authenticationState = useContext(AuthenticationContext);
  const isMobile = useMediaQuery("(max-width:576px)");
  const getLoggedUserDetails = useSelector(selectUserDetail);
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [worldAtlasFeatures, setWorldAtlasFeatures] = useState([]);
  const interactionSource = useRef("initial");
  const mapRef = useRef(null);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const swiperRef = useRef(null);

  const [state, setState] = useState({
    mediaSlides: [],
    isRetrySubscriptionPopupOpen: false,
    isRetrySubscriptionResponseType: "",
    isRetrySubscriptionMessage: "",
    directSaleRankDetails: null,
    dualTeamRankDetails: null,
    personallySponsored: null,
    totalMembers: 0,
    countryWiseCustomers: [],
    dashboardNewSignUps: [],
    dashboardCustomerOrders: [],
    dsXetaReferralLink: null,
    teamBuildingRankStatistics: null,
    currentDualTeamBonusVolumeStatistics: null,
    currDTBVolSectionVisible: false,
    currDTBVolNavigateLinkVisible: false,
    currDTBVolDatePeriod: "",
    currDTBVolLeftLeg: null,
    currDTBVolLeftLegMyPerWeekProgress: [],
    currDTBVolLeftLegCommVolWeekProgress: [],
    currDTBVolRightLeg: null,
    currDTBVolRightLegMyPerWeekProgress: [],
    currDTBVolRightLegCommVolWeekProgress: [],
  });
  
useEffect(() => {
  const resumeAutoplay = () => {
    if (swiperRef.current?.autoplay?.running === false) {
      swiperRef.current.autoplay.start();
    }
  };

  document.addEventListener("visibilitychange", resumeAutoplay);
  window.addEventListener("focus", resumeAutoplay);
  window.addEventListener("click", resumeAutoplay); 

  return () => {
    document.removeEventListener("visibilitychange", resumeAutoplay);
    window.removeEventListener("focus", resumeAutoplay);
    window.removeEventListener("click", resumeAutoplay);
  };
}, []);

  const leftLegMyPerWeekData = fillChartData(state.currDTBVolLeftLegMyPerWeekProgress);
  const leftLegCommVolWeekData = fillChartData(state.currDTBVolLeftLegCommVolWeekProgress);
  const rightLegMyPerWeekData = fillChartData(state.currDTBVolRightLegMyPerWeekProgress);
  const rightLegCommVolWeekData = fillChartData(state.currDTBVolRightLegCommVolWeekProgress);

  useEffect(() => {
    getBackofficeDashboardDetails();

    fetch(geoUrl)
      .then((res) => res.json())
      .then((topojsonData) => {
        if (topojsonData.objects.countries) {
          const geoJsonFeatures = topojsonClient.feature(
            topojsonData,
            topojsonData.objects.countries
          ).features;
          setWorldAtlasFeatures(geoJsonFeatures);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    const geo = worldAtlasFeatures.find(
      (g) => g.properties.name === selectedCountry
    );

    let newTooltipContent = "0";
    if (selectedCountry) {
      let getCountryDetails;
      if (selectedCountry === "United States of America") {
        getCountryDetails = state.countryWiseCustomers.find(
          (c) =>
            c.CountryName === "United States of America" ||
            c.CountryName === "United States"
        );
      } else {
        getCountryDetails = state.countryWiseCustomers.find(
          (c) => c.CountryName === selectedCountry
        );
      }

      if (getCountryDetails && getCountryDetails.TotalMembers != null) {
        newTooltipContent =
          getCountryDetails.TotalMembers.toLocaleString("en-US");
      }
    }
    setTooltipContent(newTooltipContent);

    if (geo) {
      if (
        (interactionSource.current === "initial" ||
          interactionSource.current === "table") &&
        mapRef.current
      ) {
        const position = getCountryCentroidSvgPosition(geo);
        setTooltipPosition(position);
      }
    } else if (!selectedCountry) {
    }
  }, [
    selectedCountry,
    worldAtlasFeatures,
    state.countryWiseCustomers,
    mapRef.current,
  ]);

  const getCountryCentroidSvgPosition = (geo) => {
    const svgWidth = 800;
    const svgHeight = 370;
    const projection = geoEqualEarth()
      .scale(150)
      .center([0, 10])
      .translate([svgWidth / 2, svgHeight / 2]);
    const [cx, cy] = projection(geoCentroid(geo));

    const svg = mapRef.current?.querySelector("svg");
    if (svg) {
      const actualWidth = svg.clientWidth;
      const actualHeight = svg.clientHeight;
      const scaleX = actualWidth / svgWidth;
      const scaleY = actualHeight / svgHeight;
      const pos = {
        x: cx * scaleX,
        y: cy * scaleY,
      };
      return pos;
    }
    return { x: cx, y: cy };
  };

  const handleCountryClick = (geo, event) => {
    interactionSource.current = "map";
    setSelectedCountry(geo.properties.name);

    if (event && event.target) {
      const card = event.target.closest(".MuiCard-root");
      if (card) {
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        setTooltipPosition({ x, y });
      } else if (mapRef.current && geo) {
        setTooltipPosition(getCountryCentroidSvgPosition(geo));
      }
    } else if (mapRef.current && geo) {
      setTooltipPosition(getCountryCentroidSvgPosition(geo));
    }
  };

  const getFlagUrl = (countryCode) =>
    `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;

  const getBackofficeDashboardDetails = async () => {
    const url = `${urlConstant.Dashboard.GetBackofficeDashboardDetails}`;
    await httpGet(
      url,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        const responseData = response.Value.Data;

        setState((prevState) => ({
          ...prevState,
          mediaSlides: responseData.MediaSlides,
          directSaleRankDetails: responseData.DirectSaleRankDetails,
          dualTeamRankDetails: responseData.DualTeamRankDetails,
          personallySponsored: responseData.PersonallySponsored,
          totalMembers: responseData.CustomerDetails.TotalMembers,
          countryWiseCustomers:
            responseData.CustomerDetails.CountryWiseCustomers,
          dashboardNewSignUps: responseData.DashboardNewSignUps,
          dashboardCustomerOrders: responseData.DashboardCustomerOrders,
          dsXetaReferralLink: responseData.DSXetaReferralLink,
          teamBuildingRankStatistics: responseData.TeamBuildingRankStatistics,
          currentDualTeamBonusVolumeStatistics:
            responseData.CurrentDualTeamBonusVolumeStatistics,
        }));

        if (
          responseData.CurrentDualTeamBonusVolumeStatistics != null &&
          responseData.CurrentDualTeamBonusVolumeStatistics.length > 0
        ) {
          setState((prevState) => ({
            ...prevState,
            currDTBVolSectionVisible:
              responseData.CurrentDualTeamBonusVolumeStatistics[0]
                .SectionVisible,
            currDTBVolNavigateLinkVisible:
              responseData.CurrentDualTeamBonusVolumeStatistics[0]
                .NavigateLinkVisible,
            currDTBVolDatePeriod: `${responseData.CurrentDualTeamBonusVolumeStatistics[0].StartDate} - ${responseData.CurrentDualTeamBonusVolumeStatistics[0].EndDate}`,
            currDTBVolLeftLeg:
              responseData.CurrentDualTeamBonusVolumeStatistics[0],
            currDTBVolRightLeg:
              responseData.CurrentDualTeamBonusVolumeStatistics[1],
            currDTBVolLeftLegMyPerWeekProgress: [
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek1,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek2,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek3,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek4,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].MyPerWeek5,
            ],
            currDTBVolLeftLegCommVolWeekProgress: [
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek1,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek2,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek3,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek4,
              responseData.CurrentDualTeamBonusVolumeStatistics[0].CommVolWeek5,
            ],
            currDTBVolRightLegMyPerWeekProgress: [
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek1,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek2,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek3,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek4,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].MyPerWeek5,
            ],
            currDTBVolRightLegCommVolWeekProgress: [
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek1,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek2,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek3,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek4,
              responseData.CurrentDualTeamBonusVolumeStatistics[1].CommVolWeek5,
            ],
          }));
        }

        let firstCountryName;
        if (
          responseData.CustomerDetails.CountryWiseCustomers != null &&
          responseData.CustomerDetails.CountryWiseCustomers.length > 0
        ) {
          firstCountryName =
            responseData.CustomerDetails.CountryWiseCustomers[0].CountryName;
          firstCountryName =
            firstCountryName === "United States"
              ? "United States of America"
              : firstCountryName;
        }

        setSelectedCountry(firstCountryName || null);
        interactionSource.current = "initial";
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const formattedMoney = (valNumber) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(valNumber);
  };

  const formatLoggedDateTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-us", {
      weekday: "short", // "Lun"
      day: "2-digit", // "10"
      month: "short", // "Mar"
      year: "numeric", // "2025"
      hour: "numeric",
      minute: "2-digit",
      hour12: true, // "9:23 PM"
    }).format(date);
    // example. Wed, May 07, 2025, 11:04 AM
  };

  const handleBuyXETAKit = () => {
    window.open(state.dsXetaReferralLink, "_blank", "noopener,noreferrer");
  };

  const handleRetryMembership = async (retrySmartShipInvoiceID) => {
    await httpGet(
      `${urlConstant.SmartShipInvoice.RetrySmartshipInvoiceByID}?Id=${retrySmartShipInvoiceID}`,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        if (response.Status === dataConstant.APIResponseStatus.Success) {
          showSnackbar(response.description, "success");
          setTimeout(() => {
            window.location.href = "/order";
          }, 2000);
        } else if (response.Status === dataConstant.APIResponseStatus.Failure) {
          setState((prevState) => ({
            ...prevState,
            isRetrySubscriptionPopupOpen: true,
            isRetrySubscriptionResponseType: "failure",
            isRetrySubscriptionMessage: response.error,
          }));
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleRetryPopupModalClose = () => {
    if (state.isRetrySubscriptionResponseType === "success") {
      window.location.reload();
    } else {
      setState((prevState) => ({
        ...prevState,
        isRetrySubscriptionPopupOpen: false,
        isRetrySubscriptionResponseType: "",
        isRetrySubscriptionMessage: "",
      }));
    }
  };

  return (
    <>
      <Box flexDirection={"column"} gap={"20px"} display={"flex"}>
        <Box width={"100%"}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
            gap={"20px"}
            flexWrap={"wrap"}
          >
            <Stack direction={"row"} alignItems={"center"} gap={"20px"}>
              <img src="/icons/star.svg" alt="user" height={52} width={60} />
              <Typography color="secondary.light" fontWeight={600} variant="h4">
                {t("Dashboard.Hello")}{" "}
                {getLoggedUserDetails?.UserAccountDetails?.PvtAcctFullName} (
                {getLoggedUserDetails?.UserAccountDetails?.AcctID}),{" "}
                {t("Dashboard.WelcomeToYourAccount")}
              </Typography>
            </Stack>
            <Typography
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              }}
              color="secondary.main"
              variant="body1"
            >
              {formatLoggedDateTime(new Date())}
            </Typography>
          </Stack>
        </Box>
            
        {getLoggedUserDetails?.PurchasePackageDetails?.IsAnyPackagePurchased ===
          false && (
          <Grid container spacing={3} sx={{ flexGrow: 1 }}>
            <Grid size={{ xs: 12, md: 12, lg: 12, xxl: 12 }}>
              <Card
                sx={(theme) => ({
                  p: "25px 30px",
                  background: theme.palette.background.cardGradient,
                  borderRadius: "20px",
                  height: "100%",
                })}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={{ xs: 2, sm: 1 }}
                  justifyContent={"space-between"}
                  flexWrap={"wrap"}
                >
                  <Typography color="secondary.light" variant="h4">
                    {t("Dashboard.ImportantNoticeTitle")}
                  </Typography>
                </Stack>
                <Card
                  className="badges-inner-sec"
                  sx={(theme) => ({
                    p: "20px",
                    my: "20px",
                    background: theme.palette.primary.lightdark,
                    borderRadius: "20px",
                  })}
                >
                  <Typography color="secondary.light" variant="h5">
                    {t("Dashboard.ImportantNoticeLine1").replace(
                      "{Expiration Date}",
                      `${moment(
                        getLoggedUserDetails?.PurchasePackageDetails
                          ?.LatestExpirationDate
                      ).format(dataConstant.DateFormatArray[0])}`
                    )}
                  </Typography>
                  <Typography color="secondary.light" variant="h5">
                    {t("Dashboard.ImportantNoticeLine2")}
                  </Typography>
                  <a
                    href={urlConstant.Dashboard.KnowledgeBaseURL}
                    target="_blank"
                    style={{
                      textDecoration: "underline",
                      textDecorationColor: "var(--primary-main)",
                    }}
                  >
                    <Typography
                      color="primary.main"
                      fontWeight={600}
                      variant="h5"
                    >
                      {t("Dashboard.ImportantNoticeLine3")}
                    </Typography>
                  </a>
                  {getLoggedUserDetails?.RetrySmartShipInvoiceID > 0 && (
                    <Button
                      sx={{
                        background: "var(--primary-main)",
                        border: "1px solid var(--headerbg)",
                        fontSize: "16px",
                        mt: "20px",
                      }}
                      variant="contained"
                      onClick={() =>
                        handleRetryMembership(
                          getLoggedUserDetails?.RetrySmartShipInvoiceID
                        )
                      }
                    >
                      {t("Dashboard.RetrySubscriptionButtonText")}
                    </Button>
                  )}
                </Card>
              </Card>
            </Grid>
          </Grid>
        )}

        {state.mediaSlides && state.mediaSlides.length > 0 && (
          <Card
            className="hero-section"
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              "& .swiper-button-next, & .swiper-button-prev": {
                WebkitTapHighlightColor: "transparent !important",
                outline: "none",
              },
            })}
            onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
            onMouseLeave={() => swiperRef.current?.autoplay?.start()}
          >
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={2}
              flexWrap={"wrap"}
            >
              {/* <Stack direction={'row'} alignItems={'center'} gap={1}>
                <img src='/icons/calendar.svg' alt='user' />
                <Card className='bg-dark' sx={{ py: 1, px: 2 }}>
                  <Typography color='secondary.light' variant='body1'>
                    Lun 10. Mar 2025, 9:23pm
                  </Typography>
                </Card>
              </Stack> */}
            </Stack>
            <Swiper
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              spaceBetween={50}
              slidesPerView={1}
              navigation
              modules={[Autoplay, Navigation, Pagination]}
              pagination={{ clickable: true }}
              scrollbar={{ draggable: true }}
              autoplay={{
                delay: 10000,
                disableOnInteraction: false,
              }}
            >
              {state.mediaSlides.map((item, index) => (
                <SwiperSlide>
                  <Typography color="secondary.light" variant="h4">
                    {item.ImageName}
                  </Typography>
                  <img
                    style={{ cursor: "pointer", maxWidth: "100%" }}
                    src={item.FileStoredPath}
                    alt={`Slide ${index + 1}`}
                    onClick={() => window.open(item.NavigationURL, "_blank")}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </Card>
        )}

        {state.dsXetaReferralLink !== null && (
          <Grid container spacing={3} sx={{ flexGrow: 1 }}>
            <Grid size={{ xs: 12, md: 12, lg: 12, xxl: 12 }}>
              <Card
                sx={(theme) => ({
                  p: "25px 30px",
                  background: theme.palette.background.cardGradient,
                  borderRadius: "20px",
                  height: "100%",
                })}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={{ xs: 2, sm: 1 }}
                  justifyContent={"space-between"}
                  flexWrap={"wrap"}
                >
                  <Typography color="secondary.light" variant="h4">
                    {t("Dashboard.Xeta.Title")}
                  </Typography>
                </Stack>
                <Card
                  className="badges-inner-sec"
                  sx={(theme) => ({
                    p: "20px",
                    my: "20px",
                    background: theme.palette.primary.lightdark,
                    borderRadius: "20px",
                  })}
                >
                  <Typography color="secondary.light" mb={"20px"} variant="h5">
                    {t("Dashboard.Xeta.Information1")}
                  </Typography>
                  <Button
                    sx={{
                      background: "var(--primary-main)",
                      border: "1px solid var(--headerbg)",
                      fontSize: "16px",
                    }}
                    variant="contained"
                    onClick={handleBuyXETAKit}
                  >
                    {t("Dashboard.Xeta.ButtonText")}
                  </Button>
                </Card>
              </Card>
            </Grid>
          </Grid>
        )}

        <Grid container spacing={3} sx={{ flexGrow: 1 }}>
          {state.directSaleRankDetails != null &&
            state.directSaleRankDetails.SectionVisible == true && (
              <Grid size={{ xs: 12, md: 6, lg: 6, xxl: 4 }}>
                <Card
                  sx={(theme) => ({
                    p: {
                      xs: "20px",
                      sm: "25px 30px",
                    },
                    background: theme.palette.background.cardGradient,
                    borderRadius: "20px",
                    height: "100%",
                  })}
                >
                  <Typography color="secondary.light" variant="h4">
                    {t("Dashboard.DirectSalesRank")}
                  </Typography>
                  <Card
                    className="badges-inner-sec"
                    sx={(theme) => ({
                      p: "20px",
                      my: "20px",
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                      flexDirection: "column",
                      [theme.breakpoints.up("sm")]: {
                        flexDirection: "row",
                      },
                    })}
                  >
                    <Card
                      sx={(theme) => ({
                        p: "10px",
                        background: theme.palette.background.cardGradient,
                        borderRadius: "20px",
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "10px",
                        flex: "1",
                      })}
                    >
                      <img src="/icons/badge1.svg" alt="user" />
                      <Stack gap={0} textAlign={"center"}>
                        <Typography
                          color="secondary.text"
                          sx={{ textTransform: "uppercase" }}
                          variant="h5"
                          fontWeight={600}
                        >
                          {state.directSaleRankDetails?.CurrentRankName}
                        </Typography>
                        <Typography
                          color="secondary.text"
                          sx={{ fontSize: "12px" }}
                          variant="body2"
                        >
                          {t("Dashboard.CurrentRank")}
                        </Typography>
                      </Stack>
                    </Card>
                    {state.directSaleRankDetails.NextRankId != null && (
                      <>
                        <img
                          src="/icons/right-arrow.svg"
                          className="arrow-right"
                          alt="user"
                        />
                        <Card
                          sx={(theme) => ({
                            p: "10px",
                            background: theme.palette.secondary.lightest,
                            borderRadius: "20px",
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "10px",
                            flex: "1",
                          })}
                        >
                          <img src="/icons/badge2.svg" alt="user" />
                          <Stack gap={0} textAlign={"center"}>
                            <Typography
                              color="common.white"
                              sx={{ textTransform: "uppercase" }}
                              variant="h5"
                              fontWeight={600}
                            >
                              {state.directSaleRankDetails?.NextRankName}
                            </Typography>
                            <Typography
                              color="secondary.text"
                              sx={{ fontSize: "12px" }}
                              variant="body2"
                            >
                              {t("Dashboard.NextRank")}
                            </Typography>
                          </Stack>
                        </Card>
                      </>
                    )}
                  </Card>
                  <Stack
                    display={
                      state.directSaleRankDetails.RankProgress > 0 ? "" : "none"
                    }
                    spacing={2}
                    sx={{ flexGrow: 1 }}
                  >
                    <BorderLinearProgress
                      variant="determinate"
                      value={state.directSaleRankDetails.RankProgress}
                    />
                  </Stack>
                </Card>
              </Grid>
            )}

          {state.dualTeamRankDetails != null &&
            state.dualTeamRankDetails.SectionVisible == true && (
              <Grid size={{ xs: 12, md: 6, lg: 6, xxl: 4 }}>
                <Card
                  sx={(theme) => ({
                    p: {
                      xs: "20px",
                      sm: "25px 30px",
                    },
                    background: theme.palette.background.cardGradient,
                    borderRadius: "20px",
                    height: "100%",
                  })}
                >
                  <Typography color="secondary.light" variant="h4">
                    {t("Dashboard.DualTeamRank")}
                  </Typography>
                  <Card
                    className="badges-inner-sec"
                    sx={(theme) => ({
                      p: "20px",
                      my: "20px",
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                      flexDirection: "column",
                      [theme.breakpoints.up("sm")]: {
                        flexDirection: "row",
                      },
                    })}
                  >
                    <Card
                      sx={(theme) => ({
                        p: "10px",
                        background: theme.palette.background.cardGradient,
                        borderRadius: "20px",
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "10px",
                        flex: "1",
                      })}
                    >
                      <img src="/icons/badge1.svg" alt="user" />
                      <Stack gap={0} textAlign={"center"}>
                        <Typography
                          color="secondary.text"
                          sx={{ textTransform: "uppercase" }}
                          variant="h5"
                          fontWeight={600}
                        >
                          {state.dualTeamRankDetails?.CurrentRankName}
                        </Typography>
                        <Typography
                          color="secondary.text"
                          sx={{ fontSize: "12px" }}
                          variant="body2"
                        >
                          {t("Dashboard.CurrentRank")}
                        </Typography>
                      </Stack>
                    </Card>
                    {state.dualTeamRankDetails.NextRankId != null && (
                      <>
                        <img
                          src="/icons/right-arrow.svg"
                          className="arrow-right"
                          alt="user"
                        />
                        <Card
                          sx={(theme) => ({
                            p: "10px",
                            background: theme.palette.secondary.lightest,
                            borderRadius: "20px",
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "10px",
                            flex: "1",
                          })}
                        >
                          <img src="/icons/badge2.svg" alt="user" />
                          <Stack gap={0} textAlign={"center"}>
                            <Typography
                              color="common.white"
                              sx={{ textTransform: "uppercase" }}
                              variant="h5"
                              fontWeight={600}
                            >
                              {state.dualTeamRankDetails?.NextRankName}
                            </Typography>
                            <Typography
                              color="secondary.text"
                              sx={{ fontSize: "12px" }}
                              variant="body2"
                            >
                              {t("Dashboard.NextRank")}
                            </Typography>
                          </Stack>
                        </Card>
                      </>
                    )}
                  </Card>
                  <Stack
                    display={
                      state.dualTeamRankDetails.RankProgress > 0 ? "" : "none"
                    }
                    direction={"row"}
                    gap={1}
                    alignItems={"center"}
                  >
                    <Typography
                      color="secondary.text"
                      variant="body2"
                      lineHeight={0.5}
                    >
                      0%
                    </Typography>
                    <Stack spacing={2} sx={{ flexGrow: 1 }}>
                      <BorderLinearProgress
                        variant="determinate"
                        value={state.dualTeamRankDetails.RankProgress}
                      />
                    </Stack>
                    <Typography
                      color="secondary.text"
                      variant="body2"
                      lineHeight={0.5}
                    >
                      100%
                    </Typography>
                  </Stack>
                </Card>
              </Grid>
            )}

          {state.personallySponsored !== null && (
            <Grid size={{ xs: 12, md: 6, lg: 6, xxl: 4 }}>
              <Card
                sx={(theme) => ({
                  p: {
                    xs: "20px",
                    sm: "25px 30px",
                  },
                  background: theme.palette.background.cardGradient,
                  borderRadius: "20px",
                  height: "100%",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  display: "flex",
                })}
              >
                <Stack>
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    gap={1}
                    alignItems={"center"}
                    flexWrap={"wrap"}
                  >
                    <Typography color="secondary.light" variant="h4">
                      {t("Dashboard.PersonalReferrals")}
                    </Typography>
                    <Typography color="secondary.text" variant="body2">
                      {state.personallySponsored} {t("Dashboard.InTotal")}
                    </Typography>
                  </Stack>
                  <Card
                    className="Referrals-inner"
                    sx={(theme) => ({
                      p: "20px",
                      my: "20px",
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    })}
                  >
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <Button
                        variant="contained"
                        color={state.personallySponsored >= 1 ? "secondary" : ""}
                        sx={{
                          px: 0.8,
                          py: 0.5,
                          backgroundColor: state.personallySponsored >= 1 ? "var(--primary-main)" : "var(--secondary-main)",
                        }}
                      >
                        <img src="/icons/user-white.svg" alt="user" />
                      </Button>
                      <Button
                        variant="contained"
                        color={state.personallySponsored >= 2 ? "secondary" : ""}
                        sx={{
                          px: 0.8,
                          py: 0.5,
                          backgroundColor: state.personallySponsored >= 2 ? "var(--primary-main)" : "var(--secondary-main)",
                        }}
                      >
                        <img src="/icons/user-white.svg" alt="user" />
                      </Button>
                      <Button
                        variant="contained"
                        color={state.personallySponsored >= 3 ? "secondary" : ""}
                        sx={{
                          px: 0.8,
                          py: 0.5,
                          backgroundColor: state.personallySponsored >= 3 ? "var(--primary-main)" : "var(--secondary-main)",
                        }}
                      >
                        <img src="/icons/user-white.svg" alt="user" />
                      </Button>
                      <Button
                        variant="contained"
                        color={state.personallySponsored >= 4 ? "secondary" : ""}
                        sx={{
                          px: 0.8,
                          py: 0.5,
                          backgroundColor: state.personallySponsored >= 4 ? "var(--primary-main)" : "var(--secondary-main)",
                          display: {
                            xs: "none",
                            sm: "inline-flex",
                          },
                        }}
                      >
                        <img src="/icons/user-white.svg" alt="user" />
                      </Button>
                      <Typography color="secondary.text" variant="h2">
                        ...
                      </Typography>
                      <Button
                        variant="contained"
                        className="button-text-white"
                        color={state.personallySponsored >= 1 ? "secondary" : ""}
                        sx={{
                          px: 0.8,
                          py: 0.5,
                          backgroundColor: state.personallySponsored >= 4 ? "var(--primary-main)" : "var(--secondary-main)",
                          fontSize: "20px",
                        }}
                      >
                        +4
                      </Button>
                    </Stack>
                  </Card>
                </Stack>
                <Stack
                  direction={"row"}
                  gap={1}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  flexWrap={"wrap"}
                >
                  {state.personallySponsored === 1 && (
                    <Typography color="common.white" variant="h4">
                      1/4
                    </Typography>
                  )}
                  {state.personallySponsored === 2 && (
                    <Typography color="common.white" variant="h4">
                      2/4
                    </Typography>
                  )}
                  {state.personallySponsored === 3 && (
                    <Typography color="common.white" variant="h4">
                      3/4
                    </Typography>
                  )}
                  {state.personallySponsored >= 4 && (
                    <Typography color="common.white" variant="h4">
                      4/4
                    </Typography>
                  )}

                  {state.personallySponsored >= 4 && (
                    <Chip
                      sx={{
                        display: "flex",
                        flexDirection: "row-reverse",
                        pr: 2.5,
                        pl: 1,
                        bgcolor: "var(--green)",
                        fontSize: { xs: "14px", sm: "22px" },
                        py: 3,
                      }}
                      label={t("Dashboard.FreeMembership")}
                      icon={<TimerOutlinedIcon />}
                    />
                  )}
                </Stack>
              </Card>
            </Grid>
          )}

          <Grid
            size={{ xs: 12, md: 6, lg: 6, xxl: 12 }}
            display={
              !state.teamBuildingRankStatistics?.SectionVisible ? "none" : ""
            }
          >
            <Card
              sx={(theme) => ({
                p: "25px 30px",
                background: theme.palette.background.cardGradient,
                borderRadius: "20px",
                height: "100%",
              })}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                gap={{ xs: 2, sm: 1 }}
                justifyContent={"space-between"}
                flexWrap={"wrap"}
              >
                <Typography color="secondary.light" variant="h4">
                  {t("Dashboard.TeamBYLDingStats")}
                </Typography>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  sx={{
                    width: {
                      xs: "100%",
                      xxl: "auto",
                    },
                    flexDirection: {
                      xs: "column",
                      sm: "row",
                    },
                  }}
                  gap={2}
                >
                  <Card
                    sx={(theme) => ({
                      background: "var(--black)",
                      py: 1,
                      px: 2,
                      textAlign: "center",
                      width: {
                        xs: "100%",
                        xxl: "auto",
                      },
                    })}
                  >
                    <Typography color="secondary.light" variant="body1">
                      {state.teamBuildingRankStatistics?.TeamBuildingRank}
                    </Typography>
                  </Card>
                  <Button
                    disableElevation
                    sx={{
                      background: "var(--black)",
                      color: "var(--white)",
                      textAlign: "center",
                      border: "1px solid var(--headerbg)",
                      py: 0.75,
                      width: {
                        xs: "100%",
                        xxl: "auto",
                      },
                      fontWeight: 500,
                    }}
                    variant="contained"
                    onClick={() => navigate("/personallySponsored")}
                  >
                    {t("Dashboard.ViewMore")}
                  </Button>
                </Stack>
              </Stack>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      my: {
                        xs: "0px",
                        sm: "20px",
                      },
                      mt: {
                        xs: "20px",
                        sm: "20px",
                      },
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    })}
                  >
                    <Stack gap={0} textAlign={"center"}>
                      <Typography
                        color="common.white"
                        sx={{ textTransform: "uppercase" }}
                        variant="h3"
                        fontWeight={600}
                      >
                        {state.teamBuildingRankStatistics?.PersonalSalesCount}
                      </Typography>
                      <Typography
                        color="secondary.text"
                        sx={{ fontSize: "12px" }}
                        variant="body2"
                      >
                        {t("Dashboard.PersonalSales")}
                      </Typography>
                    </Stack>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      my: {
                        xs: "0px",
                        sm: "20px",
                      },
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    })}
                  >
                    <Stack gap={0} textAlign={"center"}>
                      <Typography
                        color="common.white"
                        sx={{ textTransform: "uppercase" }}
                        variant="h3"
                        fontWeight={600}
                      >
                        {
                          state.teamBuildingRankStatistics
                            ?.PersonalInstallsCount
                        }
                      </Typography>
                      <Typography
                        color="secondary.text"
                        sx={{ fontSize: "12px" }}
                        variant="body2"
                      >
                        {t("Dashboard.PersonalInstalls")}
                      </Typography>
                    </Stack>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      my: {
                        xs: "0px",
                        sm: "20px",
                      },
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    })}
                  >
                    <Stack gap={0} textAlign={"center"}>
                      <Typography
                        color="common.white"
                        sx={{ textTransform: "uppercase" }}
                        variant="h3"
                        fontWeight={600}
                      >
                        {state.teamBuildingRankStatistics?.TeamSalesCount}
                      </Typography>
                      <Typography
                        color="secondary.text"
                        sx={{ fontSize: "12px" }}
                        variant="body2"
                      >
                        {t("Dashboard.TeamSales")}
                      </Typography>
                    </Stack>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      my: {
                        xs: "0px",
                        sm: "20px",
                      },
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                    })}
                  >
                    <Stack gap={0} textAlign={"center"}>
                      <Typography
                        color="common.white"
                        sx={{ textTransform: "uppercase" }}
                        variant="h3"
                        fontWeight={600}
                      >
                        {state.teamBuildingRankStatistics?.TeamInstallsCount}
                      </Typography>
                      <Typography
                        color="secondary.text"
                        sx={{ fontSize: "12px" }}
                        variant="body2"
                      >
                        {t("Dashboard.TeamInstalls")}
                      </Typography>
                    </Stack>
                  </Card>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {!isMobile && state.countryWiseCustomers.length > 0 && (
          <Grid container spacing={0} sx={{ flexGrow: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Card
                sx={(theme) => ({
                  p: {
                    xs: "20px",
                    sm: "25px 30px",
                  },
                  background: theme.palette.background.cardGradient,
                  borderRadius: "20px",
                  height: "100%",
                })}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Card
                      sx={(theme) => ({
                        p: "20px",
                        background: theme.palette.primary.lightdark,
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 2,
                        width: "100%",
                        flexDirection: "column",
                        height: "100%",
                      })}
                    >
                      <Stack
                        gap={1}
                        direction={"row"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                        width={"100%"}
                        borderBottom={"1px solid var(--headerbg)"}
                        pb={2}
                      >
                        <Typography
                          color="common.white"
                          variant="body2"
                          fontWeight={600}
                        >
                          {t("Dashboard.Members")}
                        </Typography>
                        <Typography color="secondary.text" variant="body2">
                          {state.totalMembers.toLocaleString("en-US")} in Total
                        </Typography>
                      </Stack>
                      <TableContainer
                        sx={{ background: "transparent" }}
                        className="members-table"
                        component={Paper}
                      >
                        <Table
                          sx={{ background: "transparent" }}
                          aria-label="simple table"
                        >
                          <TableHead sx={{ background: "transparent" }}>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell align="left">
                                {t("Dashboard.Country")}
                              </TableCell>
                              <TableCell align="left">
                                {t("Dashboard.Members")}
                              </TableCell>
                              <TableCell align="left">
                                {t("Dashboard.New")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {state.countryWiseCustomers &&
                            state.countryWiseCustomers.length > 0 ? (
                              state.countryWiseCustomers.slice(0, 5).map((item, index) => (
                                <TableRow
                                  key={index}
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                  }}
                                  onClick={() => {
                                    interactionSource.current = "table";
                                    const countryNameToSet =
                                      item.CountryName === "United States"
                                        ? "United States of America"
                                        : item.CountryName;
                                    setSelectedCountry(countryNameToSet);
                                  }}
                                >
                                  <TableCell>{index + 1}</TableCell>
                                  <TableCell valign="middle" align="left">
                                    <Stack
                                      direction="row"
                                      justifyContent="flex-start"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <img
                                        width={20}
                                        src={getFlagUrl(item.CountryCode)}
                                        alt={item.CountryName}
                                      />
                                      {item.CountryName}
                                    </Stack>
                                  </TableCell>
                                  <TableCell align="left">
                                    {item.TotalMembers.toLocaleString("en-US")}
                                  </TableCell>
                                  <TableCell
                                    sx={{ color: "common.green" }}
                                    align="left"
                                  >
                                    +
                                    {item.TotalNewMembers.toLocaleString(
                                      "en-US"
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  {t("Dashboard.NoDataAvailable")}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Card
                      sx={(theme) => ({
                        p: "20px",
                        background: theme.palette.common.headerbg,
                        borderRadius: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 2,
                        width: "100%",
                        flexDirection: "column",
                        height: "100%",
                        position: "relative",
                      })}
                    >
                      <ComposableMap
                        ref={mapRef}
                        projection="geoEqualEarth"
                        width={800}
                        height={370}
                        projectionConfig={{
                          scale: 150,
                          center: [0, 10],
                        }}
                        style={{ width: "100%", height: "auto" }}
                      >
                        <Geographies geography={geoUrl}>
                          {({ geographies }) =>
                            geographies.map((geo) => {
                              const isHighlighted =
                                geo.properties.name === selectedCountry;
                              let countryDetails;
                              if (
                                geo.properties.name ===
                                "United States of America"
                              ) {
                                countryDetails =
                                  state.countryWiseCustomers.find(
                                    (c) =>
                                      c.CountryName ===
                                        "United States of America" ||
                                      c.CountryName === "United States"
                                  );
                              } else {
                                countryDetails =
                                  state.countryWiseCustomers.find(
                                    (c) =>
                                      c.CountryName === geo.properties.name
                                  );
                              }
                              const members = countryDetails?.TotalMembers;
                              return (
                                <Geography
                                  key={geo.rsmKey}
                                  geography={geo}
                                  onClick={(event) =>
                                    handleCountryClick(geo, event)
                                  }
                                  style={{
                                    default: {
                                      fill: isHighlighted
                                        ? "var(--primary-main)"
                                        : "#C1C8DB",
                                      outline: "none",
                                    },
                                    hover: {
                                      fill:
                                      members > 0 ? "#0000FF" : "#828690",
                                      outline: "none",
                                    },
                                    pressed: {
                                      fill:
                                      members > 0 ? "#0000FF" : "#828690",
                                      outline: "none",
                                    },
                                  }}
                                />
                              );
                            })
                          }
                        </Geographies>
                      </ComposableMap>
                      {/* Tooltip */}
                      {tooltipContent && selectedCountry && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: tooltipPosition.y,
                            left: tooltipPosition.x,
                            transform: "translate(-50%, -100%)",
                            backgroundColor: "#0f111b",
                            color: "#fff",
                            padding: "6px 10px",
                            borderRadius: "8px",
                            boxShadow: "0px 2px 10px rgba(0,0,0,0.2)",
                            fontSize: 12,
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            pointerEvents: "none",
                          }}
                        >
                          <PersonIcon fontSize="small" />
                          <Box>
                            <div style={{ fontWeight: "bold" }}>
                              {tooltipContent}
                            </div>
                            <div style={{ fontSize: 10, opacity: 0.6 }}>
                              {t("Dashboard.Members")}
                            </div>
                          </Box>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        )}

        <Grid
          container
          spacing={0}
          sx={{ flexGrow: 1 }}
          display={!state.currDTBVolSectionVisible ? "none" : ""}
        >
          <Grid size={{ xs: 12 }}>
            <Card
              sx={(theme) => ({
                p: {
                  xs: "20px",
                  sm: "25px 30px",
                },
                background: theme.palette.background.cardGradient,
                borderRadius: "20px",
                height: "100%",
              })}
            >
              <Stack
                alignItems={"center"}
                gap={1}
                justifyContent={"space-between"}
                sx={{
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                }}
              >
                <Typography color="secondary.light" variant="h4">
                  {t("Dashboard.CurrentDualTeamBonusVolume")}
                </Typography>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  flexWrap={"wrap"}
                  gap={2}
                >
                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <img src="/icons/calendar.svg" alt="user" />
                    <Card className="bg-dark" sx={{ py: 1, px: 2 }}>
                      <Typography color="secondary.main" variant="body1">
                        {state.currDTBVolDatePeriod}
                      </Typography>
                    </Card>
                  </Stack>
                  <Button
                    sx={{
                      background: "var(--black)",
                      color: "var(--white)",
                      textAlign: "center",
                      border: "1px solid var(--headerbg)",
                      py: 0.75,
                      fontWeight: 500,
                      display: !state.currDTBVolNavigateLinkVisible ? "none" : ""
                    }}
                    variant="contained"
                    onClick={() => navigate("/binaryPlacementTree")}
                  >
                    {t("Dashboard.ViewMore")}
                  </Button>
                </Stack>
              </Stack>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      mt: "20px",
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 2,
                    })}
                  >
                    <Stack
                      gap={1.5}
                      display={"flex"}
                      justifyContent={"center"}
                      textAlign={"center"}
                      direction={"row"}
                      alignItems={"center"}
                    >
                      <Typography
                        color="common.white"
                        sx={{}}
                        variant="h4"
                        fontWeight={600}
                      >
                        {t("Dashboard.Left")}
                      </Typography>
                      <img src="/icons/left-tree.svg" alt="user" />
                    </Stack>
                    <Stack
                      gap={1}
                      alignItems={"center"}
                      width={"100%"}
                      sx={{
                        flexDirection: {
                          xs: "column",
                          sm: "row",
                        },
                      }}
                    >
                      <Stack sx={{ width: "100%" }}>
                          <Box sx={{ position: "relative", width: "100%", height: 300}}>                          
                          <CustomBarChart
                            layout="vertical"
                            className="cm-chartbar"
                            xAxis={[
                              {
                                id: "barCategories",
                                data: ["W1", "W2", "W3", "W4", "W5"],
                                scaleType: "band",
                                disableLine: true,
                                disableTicks: true,
                              },
                            ]}
                            yAxis={[
                              {
                                id: "y-axis",
                                min: 0,
                                max: getYAxisMax(leftLegMyPerWeekData),
                                disableLine: true,
                                disableTicks: true,
                                tickLabelStyle: { display: "none" },
                              },
                            ]}
                            grid={{ horizontal: false, vertical: false }}
                            series={[
                              {
                                data: Array(5).fill(getYAxisMax(leftLegMyPerWeekData)),
                                color: "#44454a",
                                borderRadius: "8px",
                                barWidth: 6,
                                barCategoryGap: 8,
                              },
                            ]}                              
                            height={300}
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              pointerEvents: "none",
                              zIndex: 1,
                            }}
                          />                          
                          <CustomBarChart
                            layout="vertical"
                            className="cm-chartbar"
                            xAxis={[
                              {
                                id: "barCategories",
                                data: ["W1", "W2", "W3", "W4", "W5"],
                                scaleType: "band",
                                disableLine: true,
                                disableTicks: true,
                              },
                            ]}
                            yAxis={[
                              {
                                id: "y-axis",
                                min: 0,
                                max: getYAxisMax(leftLegMyPerWeekData),
                                disableLine: true,
                                disableTicks: true,
                                tickLabelStyle: { display: "none" },
                              },
                            ]}
                            grid={{ horizontal: false, vertical: false }}
                            series={[
                              {
                                data: leftLegMyPerWeekData.map(d => d.display),
                                color: "var(--primary-main)",
                                borderRadius: "8px",
                                barWidth: 6,
                                barCategoryGap: 8,
                                valueFormatter: (val, context) => {                                  
                                  return leftLegMyPerWeekData[context.dataIndex]?.real ?? 0;
                                },
                              },
                            ]}                             
                            height={300}                              
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              zIndex: 2,
                              pointerEvents: "auto",                             
                            }}
                            slotProps={{
                              tooltip: {
                                trigger: "item",
                                popper: {
                                  sx: {
                                    "& .MuiChartsTooltip-paper": {
                                      backgroundColor: "transparent",
                                      boxShadow: "none",
                                    },
                                    [`& .${chartsTooltipClasses.root}`]: {
                                      border: "none",
                                      backgroundColor: "transparent",
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </Box>
                        <Stack gap={0} textAlign={"center"}>
                          <Typography
                            color="common.white"
                            sx={{ textTransform: "uppercase" }}
                            variant="h3"
                            fontWeight={600}
                          >
                            {state.currDTBVolLeftLeg?.MyPersonalCount}
                          </Typography>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                            fontWeight={600}
                          >
                            {t("Dashboard.MyPersonals")}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack sx={{ width: "100%" }}>
                          <Box sx={{ position: "relative", width: "100%", height: 300}}>                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(leftLegCommVolWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: Array(5).fill(getYAxisMax(leftLegCommVolWeekData)),
                                  color: "#44454a",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                },
                              ]}                              
                              height={300}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(leftLegCommVolWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: leftLegCommVolWeekData.map(d => d.display),
                                  color: "var(--primary-main)",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                  valueFormatter: (val, context) => {                                    
                                    return leftLegCommVolWeekData[context.dataIndex]?.real ?? 0;
                                  },
                                },
                              ]}                             
                              height={300}                              
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                zIndex: 2,
                                pointerEvents: "auto",                             
                              }}
                              slotProps={{
                                tooltip: {
                                  trigger: "item",
                                  popper: {
                                    sx: {
                                      "& .MuiChartsTooltip-paper": {
                                        backgroundColor: "transparent",
                                        boxShadow: "none",
                                      },
                                      [`& .${chartsTooltipClasses.root}`]: {
                                        border: "none",
                                        backgroundColor: "transparent",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                        <Stack gap={0} textAlign={"center"}>
                          <Typography
                            color="common.white"
                            sx={{ textTransform: "uppercase" }}
                            variant="h3"
                            fontWeight={600}
                          >
                            {state.currDTBVolLeftLeg?.CommissionVolume}
                          </Typography>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                            fontWeight={600}
                          >
                            {t("Dashboard.CommissionVolume")}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack justifyContent={"center"} display={"flex"}>
                      <Button
                        sx={{
                          background: "var(--black)",
                          color: "var(--white)",
                          border: "1px solid var(--headerbg)",
                          py: 0.5,
                          px: 2,
                          display: !state.currDTBVolNavigateLinkVisible ? "none" : "",
                        }}
                        variant="contained"
                        onClick={() => navigate("/dualTeamBonusVolume")}
                      >
                        {t("Dashboard.ViewLast30Days")}
                      </Button>
                    </Stack>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    sx={(theme) => ({
                      p: "20px",
                      mt: "20px",
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      gap: 2,
                    })}
                  >
                    <Stack
                      gap={1.5}
                      display={"flex"}
                      justifyContent={"center"}
                      textAlign={"center"}
                      direction={"row"}
                      alignItems={"center"}
                    >
                      <img src="/icons/right-tree.svg" alt="user" />
                      <Typography
                        color="common.white"
                        sx={{}}
                        variant="h4"
                        fontWeight={600}
                      >
                        {t("Dashboard.Right")}
                      </Typography>
                    </Stack>
                    <Stack
                      gap={1}
                      alignItems={"center"}
                      width={"100%"}
                      sx={{
                        flexDirection: {
                          xs: "column",
                          sm: "row",
                        },
                      }}
                    >
                      <Stack sx={{ width: "100%" }}>
                          <Box sx={{ position: "relative", width: "100%", height: 300}}>                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(rightLegMyPerWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: Array(5).fill(getYAxisMax(rightLegMyPerWeekData)),
                                  color: "#44454a",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                },
                              ]}                              
                              height={300}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(rightLegMyPerWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: rightLegMyPerWeekData.map(d => d.display),
                                  color: "var(--primary-main)",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                  valueFormatter: (val, context) => {                                    
                                    return rightLegMyPerWeekData[context.dataIndex]?.real ?? 0;
                                  },
                                },
                              ]}                             
                              height={300}                              
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                zIndex: 2,
                                pointerEvents: "auto",                             
                              }}
                              slotProps={{
                                tooltip: {
                                  trigger: "item",
                                  popper: {
                                    sx: {
                                      "& .MuiChartsTooltip-paper": {
                                        backgroundColor: "transparent",
                                        boxShadow: "none",
                                      },
                                      [`& .${chartsTooltipClasses.root}`]: {
                                        border: "none",
                                        backgroundColor: "transparent",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                        <Stack gap={0} textAlign={"center"}>
                          <Typography
                            color="common.white"
                            sx={{ textTransform: "uppercase" }}
                            variant="h3"
                            fontWeight={600}
                          >
                            {state.currDTBVolRightLeg?.MyPersonalCount}
                          </Typography>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                            fontWeight={600}
                          >
                            {t("Dashboard.MyPersonals")}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack sx={{ width: "100%" }}>
                          <Box sx={{ position: "relative", width: "100%", height: 300}}>                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(rightLegCommVolWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: Array(5).fill(getYAxisMax(rightLegCommVolWeekData)),
                                  color: "#44454a",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                },
                              ]}                              
                              height={300}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                pointerEvents: "none",
                                zIndex: 1,
                              }}
                            />                            
                            <CustomBarChart
                              layout="vertical"
                              className="cm-chartbar"
                              xAxis={[
                                {
                                  id: "barCategories",
                                  data: ["W1", "W2", "W3", "W4", "W5"],
                                  scaleType: "band",
                                  disableLine: true,
                                  disableTicks: true,
                                },
                              ]}
                              yAxis={[
                                {
                                  id: "y-axis",
                                  min: 0,
                                  max: getYAxisMax(rightLegCommVolWeekData),
                                  disableLine: true,
                                  disableTicks: true,
                                  tickLabelStyle: { display: "none" },
                                },
                              ]}
                              grid={{ horizontal: false, vertical: false }}
                              series={[
                                {
                                  data: rightLegCommVolWeekData.map(d => d.display),
                                  color: "var(--primary-main)",
                                  borderRadius: "8px",
                                  barWidth: 6,
                                  barCategoryGap: 8,
                                  valueFormatter: (val, context) => {                                    
                                    return rightLegCommVolWeekData[context.dataIndex]?.real ?? 0;
                                  },
                                },
                              ]}                             
                              height={300}                              
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                zIndex: 2,
                                pointerEvents: "auto",                             
                              }}
                              slotProps={{
                                tooltip: {
                                  trigger: "item",
                                  popper: {
                                    sx: {
                                      "& .MuiChartsTooltip-paper": {
                                        backgroundColor: "transparent",
                                        boxShadow: "none",
                                      },
                                      [`& .${chartsTooltipClasses.root}`]: {
                                        border: "none",
                                        backgroundColor: "transparent",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                        <Stack gap={0} textAlign={"center"}>
                          <Typography
                            color="common.white"
                            sx={{ textTransform: "uppercase" }}
                            variant="h3"
                            fontWeight={600}
                          >
                            {state.currDTBVolRightLeg?.CommissionVolume}
                          </Typography>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                            fontWeight={600}
                          >
                            {t("Dashboard.CommissionVolume")}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack justifyContent={"center"} display={"flex"}>
                      <Button
                        sx={{
                          background: "var(--black)",
                          color: "var(--white)",
                          border: "1px solid var(--headerbg)",
                          py: 0.5,
                          px: 2,
                          display: !state.currDTBVolNavigateLinkVisible ? "none" : "",
                        }}
                        variant="contained"
                        onClick={() => navigate("/dualTeamBonusVolume")}
                      >
                        {t("Dashboard.ViewLast30Days")}
                      </Button>
                    </Stack>
                  </Card>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={0} sx={{ flexGrow: 1 }}>
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={2}>
              {state.dashboardNewSignUps.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    sx={(theme) => ({
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 0,
                      width: "100%",
                      flexDirection: "column",
                      height: "100%",
                    })}
                  >
                    <Stack
                      gap={1}
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      width={"100%"}
                      borderBottom={"1px solid var(--headerbg)"}
                      p={"20px"}
                      flexWrap={"wrap"}
                    >
                      <Typography
                        color="common.white"
                        variant="body2"
                        fontWeight={600}
                      >
                        {t("Dashboard.RecentNewCustomerOrAffiliateSignups")}
                      </Typography>
                      <Typography color="secondary.text" variant="body2">
                        {state.dashboardNewSignUps.length > 5
                          ? "5+"
                          : state.dashboardNewSignUps.length}{" "}
                        {t("Dashboard.InTotal")}
                      </Typography>
                    </Stack>
                    <Card
                      sx={{
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(180deg, #272939 0%, #141625 100%)",
                      }}
                    >
                      <TableContainer
                        sx={{ background: "transparent" }}
                        className="background-table"
                        component={Paper}
                      >
                        <Table
                          sx={{ background: "transparent" }}
                          aria-label="simple table"
                        >
                          <TableHead sx={{ background: "transparent" }}>
                            <TableRow>
                              <TableCell align="left">
                                {t("Dashboard.CustomerName")}
                              </TableCell>
                              <TableCell align="left">
                                {t("Dashboard.SignupDate")}
                              </TableCell>
                              <TableCell align="left">
                                {t("Dashboard.UserType")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {state.dashboardNewSignUps
                              ?.slice(0, 5)
                              ?.map((item, index) => (
                                <TableRow
                                  key={index}
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  <TableCell
                                    data-title="Name"
                                    valign="middle"
                                    align="left"
                                  >
                                    {item.AcctName}
                                  </TableCell>
                                  <TableCell
                                    data-title="Signup Date"
                                    valign="middle"
                                    align="left"
                                  >
                                    {item.SignupDate
                                      ? moment(item.SignupDate).format(
                                          dataConstant.DateFormatArray[0]
                                        )
                                      : "-"}
                                  </TableCell>
                                  <TableCell
                                    data-title="User Type"
                                    align="left"
                                  >
                                    {item.UserType}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {/* <Stack
                        justifyContent={'center'}
                        display={'flex'}
                        alignItems={'center'}
                        marginBottom={'30px'}
                      >
                        <Button
                          sx={{
                            background: 'var(--black)',
                            color: 'var(--white)',
                            border: '1px solid var(--headerbg)',
                            py: 0.5,
                            px: 2.5,
                            fontSize: '16px',
                          }}
                          variant='contained'
                        >
                          {t('Dashboard.ViewAll')}
                        </Button>
                      </Stack> */}
                    </Card>
                  </Card>
                </Grid>
              )}

              {state.dashboardCustomerOrders.length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card
                    sx={(theme) => ({
                      background: theme.palette.primary.lightdark,
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 0,
                      width: "100%",
                      flexDirection: "column",
                      height: "100%",
                    })}
                  >
                    <Stack
                      gap={1}
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      width={"100%"}
                      borderBottom={"1px solid var(--headerbg)"}
                      p={"20px"}
                      flexWrap={"wrap"}
                    >
                      <Typography
                        color="common.white"
                        variant="body2"
                        fontWeight={600}
                      >
                        {t("Dashboard.RecentCustomerOrders")}
                      </Typography>
                      <Typography color="secondary.text" variant="body2">
                        {state.dashboardCustomerOrders.length > 5
                          ? "5+"
                          : state.dashboardCustomerOrders.length}{" "}
                        {t("Dashboard.InTotal")}
                      </Typography>
                    </Stack>
                    <Card
                      sx={{
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(180deg, #272939 0%, #141625 100%)",
                      }}
                    >
                      <TableContainer
                        sx={{ background: "transparent" }}
                        className="background-table"
                        component={Paper}
                      >
                        <Table
                          sx={{ background: "transparent" }}
                          aria-label="simple table"
                        >
                          <TableHead sx={{ background: "transparent" }}>
                            <TableRow>
                              <TableCell align="left">
                                {t("Dashboard.CustomerName")}
                              </TableCell>
                              <TableCell align="left">
                                {t("Dashboard.OrderTotal")}
                              </TableCell>
                              <TableCell align="left">
                                {t("Dashboard.OrderDate")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {state.dashboardCustomerOrders
                              ?.slice(0, 5)
                              ?.map((item, index) => (
                                <TableRow
                                  key={index}
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                  }}
                                >
                                  <TableCell
                                    data-title="Name"
                                    valign="middle"
                                    align="left"
                                  >
                                    {item.CustomerName}
                                  </TableCell>
                                  <TableCell
                                    data-title="Order Total"
                                    valign="middle"
                                    align="left"
                                  >
                                    {formattedMoney(item.OrderTotal)}
                                  </TableCell>
                                  <TableCell
                                    data-title="Order Date"
                                    valign="middle"
                                    align="left"
                                  >
                                    {item.OrderDate
                                      ? moment(item.OrderDate).format(
                                          dataConstant.DateFormatArray[0]
                                        )
                                      : "-"}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {/* <Stack
                        justifyContent={'center'}
                        display={'flex'}
                        alignItems={'center'}
                        marginBottom={'30px'}
                      >
                        <Button
                          sx={{
                            background: 'var(--black)',
                            color: 'var(--white)',
                            border: '1px solid var(--headerbg)',
                            py: 0.5,
                            px: 2.5,
                            fontSize: '16px',
                          }}
                          variant='contained'
                        >
                          {t('Dashboard.ViewAll')}
                        </Button>
                      </Stack> */}
                    </Card>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* RetrySubscription Popup */}
      <Dialog
        sx={{ borderRadius: "20px", background: "transparent" }}
        open={state.isRetrySubscriptionPopupOpen}
        maxWidth
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle>
            {state.isRetrySubscriptionResponseType === "success"
              ? t("Dashboard.RetrySubscriptionSuccessTitle")
              : t("Dashboard.RetrySubscriptionFailureTitle")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleRetryPopupModalClose}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={{
            background: "var(--primary-darker)!important",
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
          }}
          dividers
        >
          <Stack direction={"row"} flexWrap={"wrap"}>
            <Typography
              sx={{ fontSize: "15px", marginBottom: "10px" }}
              color={
                state.isRetrySubscriptionResponseType === "success"
                  ? "success"
                  : "error"
              }
            >
              {state.isRetrySubscriptionMessage}
            </Typography>
          </Stack>
          {state.isRetrySubscriptionResponseType === "success" && (
            <>
              <Button
                variant="contained"
                sx={{ px: 3 }}
                color="primary"
                onClick={() => {
                  window.location.href = "/order";
                }}
              >
                {t("Dashboard.RedirectOrderHistryBtn")}
              </Button>
            </>
          )}
          {state.isRetrySubscriptionResponseType === "failure" && (
            <>
              <Button
                variant="contained"
                sx={{ px: 3 }}
                color="primary"
                onClick={() => navigate("/membership")}
              >
                {t("Dashboard.ChangePaymentMethod")}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default Home;
